import {
  Grid,
  Button,
  Box,
  Typography,
  TextField,
} from "@mui/material";

import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

import { gridSpacing } from "../../store/constant";

import { Formik } from "formik";
import FormField from "../../ui-component/CustomTextField";

import * as Yup from "yup";

import DialogComponent from "../../ui-component/DialogComponent";

import { useState, useEffect } from "react";

import StockTransferService from "../../services/StockTransferService";

import CustomLoader from "../../ui-component/CustomLoader";

import { useTranslation } from "react-i18next";

import {
  assignDeviceFormFields,
  assignDeviceInitials,
} from "../../formjson/assignDevice";

import {
  convertErrorObjectToArray,
} from "../../helper";

import MainCard from "../../ui-component/cards/MainCard";

import "./form.css";


/**
 * ============================================================
 * PAGE CONSTANTS
 * ============================================================
 */

const PAGE_SIZE = 200;


/**
 * ============================================================
 * ASSIGN DEVICE
 * ============================================================
 *
 * NEW FLOW:
 *
 * Manufacturer
 *      ↓
 * Available DeviceStock
 *      ↓
 * Select Dealer
 *      ↓
 * Select Devices
 *      ↓
 * POST /api/stock/transfer/send/
 *      ↓
 * manufacturer_to_dealer register
 *      ↓
 * dealer accepts/rejects
 *
 * IMPORTANT:
 *
 * Frontend does NOT send:
 *
 * user_type
 * party_type
 * role
 * manufacturer_id
 *
 * Backend automatically resolves:
 *
 * devicemanufacture -> manufacturer
 *
 * and:
 *
 * to_type = dealer
 *
 * means:
 *
 * manufacturer -> dealer
 */
const AssignDevice = () => {

  const { t } = useTranslation();


  /**
   * ==========================================================
   * FORM CONFIG
   * ==========================================================
   */

  const [updatedFormFields, setUpdatedFormField] =
    useState(assignDeviceFormFields);


  /**
   * ==========================================================
   * UI STATE
   * ==========================================================
   */

  const [isFormLoaded, setIsFormLoaded] =
    useState(false);

  const [open, setOpen] =
    useState(false);

  const [apiError, setApiError] =
    useState(false);

  const [loading, setLoading] =
    useState(false);


  /**
   * ==========================================================
   * REGISTER / DISPATCH RESULT
   * ==========================================================
   */

  const [registerId, setRegisterId] =
    useState(null);

  const [dispatchResults, setDispatchResults] =
    useState([]);


  /**
   * ==========================================================
   * IMEI INPUT
   * ==========================================================
   *
   * Existing UI supports manually entering IMEIs.
   *
   * We still support it, but the backend requires DeviceStock
   * IDs.
   *
   * Therefore we use the available-stock response to convert:
   *
   * IMEI -> DeviceStock ID
   */

  const [imeiInput, setImeiInput] =
    useState("");


  /**
   * ==========================================================
   * ALERT STATE
   * ==========================================================
   */

  const [alert, setAlert] = useState({
    error: false,
    message: "",
    errorList: [],
  });


  /**
   * ==========================================================
   * CLOSE DIALOG
   * ==========================================================
   */

  const handleClose = () => {
    setOpen(false);
  };


  /**
   * ==========================================================
   * API ERROR HANDLER
   * ==========================================================
   */

  const getApiErrorMessage = (error) => {

    return (
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      error?.response?.data?.error ||
      error?.message ||
      "Something went wrong"
    );
  };


  /**
   * ==========================================================
   * LOAD AVAILABLE STOCK + DEALERS
   * ==========================================================
   *
   * API 1:
   *
   * POST /api/stock/transfer/available/
   *
   * For manufacturer -> dealer we send:
   *
   * {
   *   to_type: "dealer",
   *   page: 1,
   *   page_size: 200
   * }
   *
   * Backend knows that the authenticated user is:
   *
   * devicemanufacture -> manufacturer
   *
   * Therefore it returns:
   *
   * DeviceStock with stock_status = NotAssigned
   *
   *
   * API 2:
   *
   * POST /api/stock/transfer/destinations/
   *
   * {
   *   to_type: "dealer"
   * }
   *
   * Backend returns only dealers belonging to this manufacturer.
   */
  const loadTransferData = async () => {

    setLoading(true);

    setApiError(false);

    try {

      /**
       * ======================================================
       * LOAD AVAILABLE DEVICES
       * ======================================================
       */

      const stockResponse =
        await StockTransferService.getAvailableStock({
          to_type: "dealer",
          page: 1,
          page_size: PAGE_SIZE,
        });


      const stockData =
        stockResponse?.data || {};


      const stockResults =
        Array.isArray(stockData.results)
          ? stockData.results
          : [];


      /**
       * Convert API stock into FormField options.
       *
       * IMPORTANT:
       *
       * value = DeviceStock ID
       *
       * label = IMEI
       *
       * This ensures the final payload contains:
       *
       * ids: [55, 56, 57]
       */

      const deviceOptions =
        stockResults.map((device) => {

          const deviceId =
            device?.id;

          const imei =
            device?.imei ||
            device?.imei_no ||
            device?.IMEI ||
            "";

          const label =
            imei ||
            device?.uid ||
            device?.device_esn ||
            `Device ${deviceId}`;


          return {
            value: deviceId,
            label,
            id: deviceId,
            imei_no: imei,
            imei,
            raw: device,
          };

        });


      /**
       * ======================================================
       * LOAD DEALERS
       * ======================================================
       */

      const destinationResponse =
        await StockTransferService.getTransferDestinations({
          to_type: "dealer",
        });


      const destinations =
        destinationResponse?.data?.destinations || [];


      /**
       * Convert destinations into form options.
       */

      const dealerOptions =
        destinations.map((dealer) => {

          return {
            value: dealer.id,
            label:
              dealer.name ||
              `Dealer ${dealer.id}`,

            id: dealer.id,

            name:
              dealer.name ||
              `Dealer ${dealer.id}`,

            type:
              dealer.type ||
              "dealer",
          };

        });


      /**
       * ======================================================
       * UPDATE FORM CONFIG
       * ======================================================
       */

      setUpdatedFormField((prevConfig) => ({

        ...prevConfig,

        dealer: {
          ...prevConfig.dealer,
          options: dealerOptions,
        },

        device: {
          ...prevConfig.device,
          options: deviceOptions,
        },

      }));


      /**
       * No stock information
       */

      if (!deviceOptions.length) {

        setAlert({
          error: false,
          message:
            "No available devices found.",
          errorList: [],
        });

      }


      /**
       * No dealers
       */

      if (!dealerOptions.length) {

        setAlert({
          error: true,
          message:
            "No dealer destinations are available.",
          errorList: [],
        });

      }


      setIsFormLoaded(true);


    } catch (error) {

      console.error(
        "Unified transfer data error:",
        error
      );


      setApiError(true);

      setAlert({
        error: true,
        message: getApiErrorMessage(error),
        errorList: convertErrorObjectToArray(
          getApiErrorMessage(error)
        ),
      });

      setIsFormLoaded(true);


    } finally {

      setLoading(false);

    }
  };


  /**
   * ==========================================================
   * INITIAL LOAD
   * ==========================================================
   */

  useEffect(() => {

    loadTransferData();

  }, []);


  /**
   * ==========================================================
   * FILE CHANGE
   * ==========================================================
   *
   * Kept for compatibility with your existing FormField.
   */
  const handleFileChange = (
    event,
    formik
  ) => {

    const selectedFile =
      event.target.files[0];

    const fieldName =
      event.target.name;

    if (selectedFile) {

      formik.setFieldValue(
        fieldName,
        selectedFile
      );

    }

  };


  /**
   * ==========================================================
   * VALIDATION
   * ==========================================================
   */

  const validationSchema =
    Yup.object(
      Object.keys(
        updatedFormFields
      ).reduce(
        (acc, field) => {

          if (
            updatedFormFields[field]
              ?.validation
          ) {

            acc[field] =
              updatedFormFields[field]
                .validation;

          }

          return acc;

        },
        {}
      )
    );


  /**
   * ==========================================================
   * HANDLE IMEI TEXTBOX
   * ==========================================================
   *
   * Converts manually entered IMEIs into DeviceStock IDs.
   */
  const handleImeiInputChange = (
    value,
    formik
  ) => {

    setImeiInput(value);


    const imeiArray =
      value
        .split(",")
        .map(
          (imei) =>
            imei.trim()
        )
        .filter(Boolean);


    const availableDevices =
      updatedFormFields
        ?.device
        ?.options || [];


    /**
     * Find DeviceStock rows using IMEI.
     */

    const matchedDevices =
      availableDevices.filter(
        (device) =>
          imeiArray.includes(
            String(
              device?.imei_no
            )
          ) ||
          imeiArray.includes(
            String(
              device?.imei
            )
          ) ||
          imeiArray.includes(
            String(
              device?.label
            )
          )
      );


    /**
     * Existing selections.
     */

    const existingSelected =
      Array.isArray(
        formik.values.device
      )
        ? formik.values.device
        : formik.values.device
        ? [formik.values.device]
        : [];


    /**
     * Merge existing + manually matched.
     *
     * These are DeviceStock IDs.
     */

    const mergedSelection = [
      ...new Set([
        ...existingSelected,
        ...matchedDevices.map(
          (device) =>
            device.value
        ),
      ]),
    ];


    formik.setFieldValue(
      "device",
      mergedSelection
    );

  };


  /**
   * ==========================================================
   * HANDLE SUBMIT
   * ==========================================================
   *
   * NEW API:
   *
   * POST /api/stock/transfer/send/
   *
   * Manufacturer -> Dealer
   *
   * Payload:
   *
   * {
   *   ids: [55, 56],
   *   to_type: "dealer",
   *   to_id: 10,
   *   dispatch_date: "2026-08-14",
   *   comments: "..."
   * }
   *
   * NO:
   *
   * user_type
   * role
   * manufacturer_id
   * manufacturer_name
   */
  const handleSubmit = async (
    values,
    {
      setSubmitting,
      resetForm,
    }
  ) => {

    setSubmitting(true);

    setLoading(true);

    setApiError(false);


    try {

      /**
       * ======================================================
       * SELECTED DEVICE IDS
       * ======================================================
       */

      const selectedDevices =
        Array.isArray(values.device)
          ? values.device
          : values.device
          ? [values.device]
          : [];


      /**
       * Convert IDs to numbers where possible.
       */

      const deviceIds =
        selectedDevices
          .map((id) => {

            const numericId =
              Number(id);

            return Number.isNaN(
              numericId
            )
              ? id
              : numericId;

          })
          .filter(
            (id) =>
              id !== null &&
              id !== undefined &&
              id !== ""
          );


      /**
       * ======================================================
       * VALIDATE DEVICES
       * ======================================================
       */

      if (!deviceIds.length) {

        setAlert({
          error: true,
          message:
            "Please select at least one device.",
          errorList: [],
        });

        setOpen(true);

        return;
      }


      /**
       * ======================================================
       * VALIDATE DEALER
       * ======================================================
       */

      if (!values.dealer) {

        setAlert({
          error: true,
          message:
            "Please select a dealer.",
          errorList: [],
        });

        setOpen(true);

        return;
      }


      /**
       * ======================================================
       * DISPATCH DATE
       * ======================================================
       *
       * API requires:
       *
       * YYYY-MM-DD
       *
       * We automatically use today's date.
       */

      const dispatchDate =
        new Date()
          .toISOString()
          .split("T")[0];


      /**
       * ======================================================
       * FINAL UNIFIED PAYLOAD
       * ======================================================
       *
       * THIS IS THE IMPORTANT PART.
       *
       * No user type is sent.
       */

      const payload = {

        /**
         * DeviceStock IDs
         */
        ids: deviceIds,

        /**
         * Manufacturer -> Dealer
         *
         * Backend determines caller:
         *
         * devicemanufacture -> manufacturer
         */
        to_type: "dealer",

        /**
         * Destination dealer ID
         */
        to_id: Number(values.dealer),

        /**
         * Required dispatch date
         */
        dispatch_date: dispatchDate,

        /**
         * Existing shipping remark
         * becomes comments
         */
        comments:
          values.shipping_remark ||
          "",

        /**
         * Optional fields
         *
         * Leave empty unless you later
         * add them to the UI.
         */
        tracking_no: "",

        dispatch_mode: "",
      };


      /**
       * Debug payload
       *
       * You can remove this later.
       */
      console.log(
        "Unified Dispatch Payload:",
        payload
      );


      /**
       * ======================================================
       * NEW API CALL
       * ======================================================
       */

      const response =
        await StockTransferService.sendTransfer(
          payload
        );


      const responseData =
        response?.data || {};


      /**
       * ======================================================
       * SAVE REGISTER ID
       * ======================================================
       */

      setRegisterId(
        responseData.register_id ||
        null
      );


      /**
       * ======================================================
       * SAVE ITEM RESULTS
       * ======================================================
       */

      const results =
        Array.isArray(
          responseData.results
        )
          ? responseData.results
          : [];


      setDispatchResults(
        results
      );


      /**
       * ======================================================
       * SUCCESS / ERROR COUNTS
       * ======================================================
       */

      const successCount =
        results.filter(
          (item) =>
            item.status ===
            "success"
        ).length;


      const errorCount =
        results.filter(
          (item) =>
            item.status ===
            "error"
        ).length;


      /**
       * ======================================================
       * MESSAGE
       * ======================================================
       */

      if (
        successCount > 0 &&
        errorCount > 0
      ) {

        setAlert({
          error: false,

          message:
            `${successCount} device(s) dispatched successfully. ` +
            `${errorCount} device(s) failed.`,

          errorList:
            results
              .filter(
                (item) =>
                  item.status ===
                  "error"
              )
              .map(
                (item) =>
                  `Device ${
                    item.id ||
                    item.item_id ||
                    ""
                  }: ${
                    item.error ||
                    "Dispatch failed"
                  }`
              ),
        });

      } else if (
        successCount > 0
      ) {

        setAlert({
          error: false,

          message:
            `${successCount} device(s) dispatched successfully. ` +
            `Waiting for dealer acceptance.`,

          errorList: [],
        });

      } else {

        setAlert({
          error: true,

          message:
            "No device was dispatched.",

          errorList:
            results.map(
              (item) =>
                item.error ||
                "Dispatch failed"
            ),
        });

      }


      /**
       * ======================================================
       * SHOW RESULT
       * ======================================================
       */

      setOpen(true);


      /**
       * ======================================================
       * RESET FORM
       * ======================================================
       */

      resetForm({
        values: assignDeviceInitials,
      });

      setImeiInput("");


      /**
       * ======================================================
       * REFRESH AVAILABLE STOCK
       * ======================================================
       *
       * Successfully dispatched devices should no longer
       * appear in available stock because they are now
       * in transit.
       */

      await loadTransferData();


    } catch (error) {

      console.error(
        "Unified dispatch error:",
        error
      );


      const message =
        getApiErrorMessage(
          error
        );


      /**
       * Network error
       */

      if (
        error.message ===
        "Network Error"
      ) {

        setApiError(true);

      }


      /**
       * API error
       */

      setAlert({
        error: true,
        message,
        errorList:
          convertErrorObjectToArray(
            message
          ),
      });


      setOpen(true);


    } finally {

      setLoading(false);

      setSubmitting(false);

    }

  };


  /**
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <>

      {/* ======================================================
          RESULT DIALOG
      ====================================================== */}

      <DialogComponent
        open={open}
        handleClose={handleClose}
        message={
          registerId
            ? `${alert.message} Register ID: ${registerId}`
            : alert.message
        }
        errorList={
          alert.errorList
        }
      />


      {/* ======================================================
          PAGE
      ====================================================== */}

      <Grid
        container
        spacing={gridSpacing}
      >

        {loading && (
          <CustomLoader />
        )}


        {/* ====================================================
            SERVER ERROR
        ==================================================== */}

        {apiError && (

          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: "14px",
              width: "100%",
            }}
          >

            <AlertTitle>
              {t(
                "common.internalServerError"
              )}
            </AlertTitle>

            {t(
              "common.serverErrorContactAdmin"
            )}

          </Alert>

        )}


        <Grid
          item
          xs={12}
        >

          <MainCard>

            <Box
              sx={{
                p: 4,
                borderRadius: "24px",
                background:
                  "linear-gradient(180deg,#ffffff,#f8fafc)",
                border:
                  "1px solid #e2e8f0",
              }}
            >

              <Typography
                variant="h3"
                sx={{
                  mb: 4,
                  fontWeight: 800,
                  color: "#0f172a",
                }}
              >
                {t(
                  "assignDeviceForm.title"
                )}
              </Typography>


              {isFormLoaded && (

                <Formik

                  initialValues={
                    assignDeviceInitials
                  }

                  validationSchema={
                    validationSchema
                  }

                  onSubmit={
                    handleSubmit
                  }

                  enableReinitialize

                >

                  {(formik) => (

                    <form
                      onSubmit={
                        formik.handleSubmit
                      }
                    >

                      <Grid
                        container
                        columnSpacing={3}
                        rowSpacing={0}
                      >


                        {/* =================================================
                            EXISTING DEALER + DEVICE + REMARK FIELDS
                        ================================================= */}

                        {Object.keys(
                          updatedFormFields
                        ).map(
                          (field) => {

                            const fieldData =
                              updatedFormFields[
                                field
                              ];


                            return (

                              <Grid
                                key={field}
                                item
                                xs={12}
                                sm={6}
                                md={4}
                              >

                                <Typography
                                  sx={{
                                    fontSize:
                                      "14px",
                                    fontWeight:
                                      700,
                                    color:
                                      "#334155",
                                    minHeight:
                                      "42px",
                                    display:
                                      "flex",
                                    alignItems:
                                      "flex-end",
                                    wordBreak:
                                      "break-word",
                                  }}
                                >

                                  {t(
                                    fieldData.label
                                  )}

                                </Typography>


                                <Box
                                  sx={{
                                    "& .MuiInputLabel-root":
                                      {
                                        display:
                                          "none",
                                      },

                                    "& .MuiOutlinedInput-root":
                                      {
                                        borderRadius:
                                          "14px",
                                        background:
                                          "#fff",
                                        minHeight:
                                          "54px",
                                      },

                                    "& input":
                                      {
                                        padding:
                                          "14px",
                                      },

                                    "& .MuiButton-root":
                                      {
                                        borderRadius:
                                          "14px",
                                      },

                                    "& a, & span":
                                      {
                                        color:
                                          "#334155 !important",
                                      },
                                  }}
                                >

                                  <FormField
                                    fieldConfig={{
                                      ...fieldData,
                                      label: "",
                                    }}
                                    formik={
                                      formik
                                    }
                                    handleFileChange={
                                      handleFileChange
                                    }
                                  />

                                </Box>

                              </Grid>

                            );

                          }
                        )}


                        {/* =================================================
                            IMEI INPUT
                        ================================================= */}

                        <Grid
                          item
                          xs={12}
                        >

                          <Typography
                            sx={{
                              fontSize:
                                "14px",
                              fontWeight:
                                700,
                              color:
                                "#334155",
                              mb: 1,
                              mt: 2,
                            }}
                          >
                            Enter Comma Separated
                            IMEI Numbers
                          </Typography>


                          <TextField

                            fullWidth

                            multiline

                            rows={4}

                            placeholder={
                              "123456789012345,987654321098765"
                            }

                            value={
                              imeiInput
                            }

                            onChange={
                              (e) =>
                                handleImeiInputChange(
                                  e.target.value,
                                  formik
                                )
                            }

                            helperText={
                              "Only devices available for dispatch can be selected."
                            }

                            sx={{
                              "& .MuiOutlinedInput-root":
                                {
                                  borderRadius:
                                    "14px",
                                  background:
                                    "#fff",
                                },
                            }}

                          />

                        </Grid>


                        {/* =================================================
                            SUBMIT
                        ================================================= */}

                        <Grid
                          item
                          xs={12}
                        >

                          <Box
                            sx={{
                              mt: 2,
                            }}
                          >

                            <Button

                              type="submit"

                              variant="contained"

                              disabled={
                                loading ||
                                formik.isSubmitting
                              }

                              sx={{
                                height:
                                  "56px",
                                px: 5,
                                borderRadius:
                                  "16px",
                                fontWeight:
                                  800,
                                fontSize:
                                  "16px",
                                textTransform:
                                  "none",
                                background:
                                  "linear-gradient(90deg,#14b8a6,#0ea5e9)",
                                boxShadow:
                                  "0 14px 30px rgba(14,165,233,.25)",
                              }}

                            >

                              {loading
                                ? "Dispatching..."
                                : t(
                                    "common.submit"
                                  )}

                            </Button>

                          </Box>

                        </Grid>

                      </Grid>

                    </form>

                  )}

                </Formik>

              )}

            </Box>

          </MainCard>

        </Grid>

      </Grid>

    </>
  );
};


export default AssignDevice;